// A local stand-in for the <iconify-icon> web component.
//
// The vendored AppShell and Assistant draw their icons as
// <iconify-icon icon="ph:list-duotone">, which in the design-system previews
// is backed by the Iconify script fetching SVGs from api.iconify.design. This
// app loads nothing from another host, so this element renders the same
// Phosphor Duotone icon from the bundled @phosphor-icons/react instead, at
// 1em (the components size it with font-size). Unknown names render nothing.
import React from 'react';
import { createRoot } from 'react-dom/client';
import { iconComponent } from './icons.jsx';

export function parseIconName(value) {
  const m = /^ph:([a-z0-9-]+?)(?:-(duotone|bold|fill|light|thin|regular))?$/.exec(String(value || ''));
  return m ? { name: m[1], weight: m[2] || 'regular' } : null;
}

export function defineIconifyShim() {
  if (typeof window === 'undefined' || !window.customElements) return;
  if (window.customElements.get('iconify-icon')) return;

  class LocalIconifyIcon extends HTMLElement {
    static get observedAttributes() { return ['icon']; }

    connectedCallback() {
      if (!this.style.display) this.style.display = 'inline-block';
      this.style.lineHeight = '0';
      this.render();
    }

    attributeChangedCallback() {
      if (this.isConnected) this.render();
    }

    disconnectedCallback() {
      const root = this._root;
      this._root = null;
      // Unmount after the current render pass, never during it.
      if (root) setTimeout(() => root.unmount(), 0);
    }

    render() {
      const parsed = parseIconName(this.getAttribute('icon'));
      const C = parsed && iconComponent(parsed.name);
      if (!this._root) this._root = createRoot(this);
      this._root.render(C ? React.createElement(C, { size: '1em', weight: parsed.weight, 'aria-hidden': true }) : null);
    }
  }

  window.customElements.define('iconify-icon', LocalIconifyIcon);
}
